import React, { Component, Context, useContext } from "react";
import { Route, Switch, NavLink } from "react-router-dom";
import Geocode from "react-geocode";
import IconButton from "@material-ui/core/IconButton";
import ArrowDownIcon from "@material-ui/icons/ArrowDropDown";
import clsx from "clsx";
import { ICustomStoreConnectedProps, connectCustom } from "../../redux/actions";
import AuthContext, { IAuthProps, withAuth } from "../../laco-common/auth/AuthContext";
import { FirebaseProvider } from "../../laco-common/firebase/context";
import Header from "../layout/Header";
import { ISitePage } from "../../laco-common/base/models/page";
import { ISiteWidget } from "../../models/cms";
import { WidgetProps, Widgets } from "../widgets";
import { UIPageTitle } from "../ui/UIPageTitle";
import Footer from "../layout/Footer";
import { DEBUG_MODE } from "../../utils/developer";
import { PSRole, getPSRole } from "../../models/peer-support";
import Home from "./Home";
import UILoader from "../ui/UILoader";
import { IForm } from "../../laco-common";
import NotificationsService from "../../laco-common/services/notifications";
import LayoutContext, { ILayoutContext } from "../../contexts/layout/LayoutContext";
import { IUserAuthPayload } from "../../laco-common/base/models/user";
import { createMonthlyReport } from "../../laco-common/base/firestore-utils";
import { IAuthContext } from "../../laco-common/base/auth";
import { FIREWIRE_TENANT } from "../../laco-common/base/models/tenant";
import { firewireApp } from "../../firewire";
import ClientFirebase from "../../laco-common/firebase/web";
import PS_LOGO from "../../laco-common/assets/images/PS-logo.png";
import { WidgetType } from "../../laco-common/base/models/widget";
import "./PeerSupport.scss";
import PreviewIcon from "@material-ui/icons/VisibilityOutlined";

export const IS_STANDALONE_PS_SITE: boolean = process.env.REACT_APP_BUILD_TARGET === "ps";
console.log("IS_STANDALONE_PS_SITE:", IS_STANDALONE_PS_SITE);
export const IS_EMBED: boolean = window.location != window.parent?.location;
const PEER_SUPPORT_MONTHLY_FORM_ID: string = "peer-support-monthly";

Geocode.setApiKey(process.env.REACT_APP_GOOGLE_MAPS_API);
Geocode.setLanguage("en");
Geocode.setRegion("US");
if (DEBUG_MODE) Geocode.enableDebug();

type PSNavigation = ISitePage & { widget?: ISiteWidget; exact: boolean };

type PeerSupportState = {
	navigation: ISitePage[];
	collapsedNav: boolean;
};

class PeerSupport extends Component<ICustomStoreConnectedProps & IAuthProps & WidgetProps, PeerSupportState> {
	static contextType: Context<ILayoutContext> = LayoutContext;
	private _historySub = null;

	state = {
		navigation: this.getNavigation(),
		collapsedNav: true,
	};

	componentDidMount() {
		if (!IS_STANDALONE_PS_SITE) {
			this.props.authStoreDispatch.login(this.props.authContext);
			this.props.peerSupportStoreDispatch.loadAgents();
		}
		this.props.peerSupportStoreDispatch.loadFacilities();
		this._historySub = this.props.history.listen((location, action) => {
			this.init(location);
		});
		this.init();
	}

	componentWillUnmount() {
		if (this._historySub()) this._historySub();
	}

	componentDidUpdate(prevProps: ICustomStoreConnectedProps & IAuthProps, prevState, snapshot) {
		if (
			prevProps.authContext.uid !== this.props.authContext.uid ||
			prevProps.peerSupportStore.agents !== this.props.peerSupportStore.agents ||
			prevProps.appStore.pages?.length !== this.props.appStore.pages?.length
		) {
			this.setState({ navigation: this.getNavigation() });
		}
	}

	init(l?) {
		const location = window.location.pathname.substr(1);
		const page: ISitePage = this.props.appStore.pages.find((p) => p.path === location);
		if (page) {
			this.props.appStoreDispatch.setCurrentPage(page, true, true);
		}
	}

	getNavigation(): ISitePage[] {
		const userRole: PSRole = getPSRole(this.props.authContext, ClientFirebase, this.props.peerSupportStore.agents);
		const navigation: ISitePage[] =
			this.props.appStore.pages.filter((p) => p.parentPage === "page-peer-support") || [];
		const userOkRoles: PSRole[] = [];
		switch (userRole) {
			case "admin": {
				userOkRoles.push("admin");
			}
			case "agent": {
				userOkRoles.push("agent");
			}
			case "member": {
				userOkRoles.push("member");
			}
			case "guest": {
				break;
			}
		}
		return navigation.filter((p) => {
			return !p.minPSRole || userOkRoles.includes(p.minPSRole);
		});
	}

	onMonthlyReportSubmitted(
		formId: string,
		values: any,
		formConfig: IForm,
		userPayload: IUserAuthPayload,
		originalValues
	) {
		console.log("onMonthlyReportSubmitted:", values, formConfig, originalValues);
		const report = createMonthlyReport(formId, values, formConfig, userPayload, originalValues);
		return this.props.peerSupportStoreDispatch.submitMonthlyReport(report).then(() => {
			NotificationsService.success("Your report has been submitted.");
		});
	}

	renderInternalNav() {
		return (
			<div className={clsx("peer-inner-navbar-container", !IS_EMBED && "container-fluid")}>
				<div className="container">
					<nav className={clsx("navbar internal-nav", IS_EMBED && "embedded-view-nav")}>
						<ul
							className={clsx(
								"navbar-nav internal-nav-menu",
								this.state.collapsedNav && !this.context.settings.isXLRes && "collapsed"
							)}
						>
							{this.state.navigation.map((page: PSNavigation, i: number) => {
								return (
									<React.Fragment key={i}>
										{!!page.path && page.active && (
											<NavLink
												to={`/${i === 0 && this.props.isNested ? "peer-support" : page.path}`}
												className="nav-item"
												activeClassName="selected"
												exact
												onClick={
													!this.context.settings.isXLRes
														? (e) => {
																this.setState({ collapsedNav: true });
														  }
														: undefined
												}
											>
												{page.navTitle || page.title}
											</NavLink>
										)}
									</React.Fragment>
								);
							})}

							{!this.context.settings.isXLRes && (
								<IconButton
									className="ps-navbar-toggle"
									aria-label="Toggle Menu"
									onClick={() => {
										this.setState({ collapsedNav: !this.state.collapsedNav });
									}}
								>
									<ArrowDownIcon fontSize="large" />
								</IconButton>
							)}
						</ul>
					</nav>
				</div>
			</div>
		);
	}

	render() {
		return (
			<FirebaseProvider
				value={{
					clientFirebase: firewireApp as any,
					firewireApp,
					tenant: FIREWIRE_TENANT,
					userPayload: this.props.authContext.userPayload,
				}}
			>
				<div className="light-mode1">
					{!this.props.isNested && !IS_EMBED && (
						<Header
							isSimpleHeader
							logoSrc={IS_STANDALONE_PS_SITE ? PS_LOGO : undefined}
							navProps={{
								hideDonate: true,
								overrideNavigation: this.state.navigation,
								// className: "ml-auto mr-4",
							}}
							authSignInProps={{ hideDonate: true, lightMode: false, btnRegMode: true }}
							omitSignIn={IS_STANDALONE_PS_SITE}
						>
							{/* <NavLink to="/">Back to site</NavLink> */}
						</Header>
					)}
					{IS_EMBED && (
						<h3 className="float-md-left mb-1 mr-5 mt-1 text-primary" style={{ fontSize: "21px" }}>
							<NavLink to="/ps">
								<img src={PS_LOGO} width={40} className="mr-1" />
								Peer Logic
							</NavLink>
						</h3>
					)}
					{(this.props.isNested || IS_EMBED) && this.renderInternalNav()}

					<div className={clsx(!IS_EMBED && "ps-container")} style={{ clear: "both" }}>
						{!IS_STANDALONE_PS_SITE && this.props.authContext.loading && <UILoader display={true} />}
						{(IS_STANDALONE_PS_SITE || !this.props.authContext.loading) && (
							<Switch>
								{this.state.navigation.map((nav: PSNavigation, i: number) => (
									<Route
										key={nav.id}
										path={`/${nav.path}`}
										exact={i === 0 || nav.psFormId === PEER_SUPPORT_MONTHLY_FORM_ID}
										render={(props) => {
											return (
												<>
													<UIPageTitle pageTitle={nav.metaTitle} />
													<div className="animated fadeIn fast-1">
														<Widgets
															widgets={nav.widgets?.map((w) => {
																if (
																	w === "form" &&
																	nav.psFormId === PEER_SUPPORT_MONTHLY_FORM_ID
																) {
																	return {
																		id: "ps-monthly-report",
																		widgetType: w as WidgetType,
																		params: PEER_SUPPORT_MONTHLY_FORM_ID,
																		isNested: true,
																		widgetProps: {
																			submitBoth: true,
																			onFormSubmit:
																				this.onMonthlyReportSubmitted.bind(
																					this
																				),
																		},
																	};
																}
																return {
																	id: "",
																	widgetType: w as WidgetType,
																	isNested: true,
																};
															})}
															page={nav}
														/>
													</div>
												</>
											);
										}}
									/>
								))}
								<Route
									render={() => {
										// default to home page
										return (
											<>
												<UIPageTitle
													pageTitle={IS_STANDALONE_PS_SITE ? "Peer Logic" : "Peer Support"}
												/>
												<div className="animated fadeIn fast-1">
													<Home page={null} widget={null} isNested={true} />
												</div>
											</>
										);
									}}
								/>
							</Switch>
						)}
					</div>
					{!this.props.isNested && !IS_EMBED && (
						<Footer page={null} bottomOnly={true} omitDisclaimer={IS_STANDALONE_PS_SITE} />
					)}
				</div>
			</FirebaseProvider>
		);
	}
}

export default connectCustom<WidgetProps>(withAuth(PeerSupport), ["appStore", "authStore", "peerSupportStore"], true);

export type PSAdminContentProps = {
	uid?: string;
	children: React.ReactNode;
};

export const PSAdminContent: React.FC<PSAdminContentProps> = (props: PSAdminContentProps) => {
	const context: IAuthContext = useContext(AuthContext);
	if (context.userPayload?.superAdmin || context.userPayload?.psAdmin || (context.uid && context.uid === props.uid))
		return <React.Fragment key={context.uid}>{props.children}</React.Fragment>;
	return null;
};
